import { LayerTypeDTO } from 'business/models/Models';
import { ButtonsListContainer } from 'components/Button';
import { RMCustomButtonProps } from 'components/customComponents/CommonComponents/RMCustomButton/RMCustomButton';
import CheckBox from 'components/customComponents/Experiment/ImagingSchedule/CheckBox';
import { LabelRadioBox } from 'components/customComponents/Layers/InputGroups';
import { IndexDBBuffer } from 'components/plateviewer/experiment/IndexDBBuffer';
import { PreparedStocksWarningModal } from 'components/plateviewer/experiment/PreparedStocksWarningModal';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useRedoExperiment, useRMSelector, useUndoExperiment } from 'redux/hooks';
import {
  headerButtonsColor,
  headerIconSize,
  LayerType,
  isCtrlKeyPressed,
  showAlertSnackBar,
} from 'utils/Global';
import useCopy, { useCopyParams } from './Hooks/ExperimentCommands/useCopy';
import usePaste, { usePasteParams } from './Hooks/ExperimentCommands/usePaste';
import { IStyle } from 'Styles';
import ETreeNodeAccessRightNames from 'enums/ETreeNodeAccessRightNames';
import { EProductEditions } from 'enums/EProductEditions';
import { RMModal } from 'components/modal';
import { ExpDashboardContext } from './ExperimentDashboardContext';
import { KeyboardKeys } from 'constants/';
import { getNormalizedKeyboardKey } from 'keyboardShortcuts/utils';
import { CutCopyOperationType } from 'components/Experiment/constants';
import { selectIsLayerSelected } from 'redux/selectors/ExperimentStateSelector';
import { useEventListener } from 'CustomHooks/useEventListener';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';

const LAYER_TYPES = [
  { value: LayerTypeDTO.Grid, name: 'Grid Layer' },
  { value: LayerTypeDTO.ConditionList, name: 'Custom Layer' },
  { value: LayerTypeDTO.Random, name: 'Random Layer' },
];
const BUFFER = new IndexDBBuffer();
const ExperimentCommandButtons = ({
  isDisabled = true,
  isPasteNotAllowed = false,
  setExperimentClipboardState,
}) => {
  const [copyType, setCopyType] = useState('');
  const [showPasteModal, setShowPasteModal] = useState<boolean>(false);
  const [pasteNewLayer, setPasteNewLayer] = useState<boolean>(true);
  const [stocksNotFound, setStocksNotFound] = useState<string[]>([]);
  const [showStocksModal, setShowStocksModal] = useState<boolean>(false);
  const [subStock, setSubStock] = useState<boolean>(true);
  const [selectedLayerId, setSelectedLayerId] = useState<number>(0);
  const [selectedLayerType, setSelectedLayerType] = useState(LAYER_TYPES[0].value);
  const [pasteDisable, setPasteDisable] = useState<boolean>(true);
  const layerOperationType = React.useRef<CutCopyOperationType>();

  const setLayerOperationType = (type: CutCopyOperationType) => {
    layerOperationType.current = type;
  };
  const pasteParams: usePasteParams = useMemo(
    () => ({
      copyType,
      setShowPasteModal,
      pasteNewLayer,
      selectedLayerId,
      selectedLayerType,
      setShowStocksModal,
      setStocksNotFound,
      stocksNotFound,
      subStock,
      layerOperationType: layerOperationType?.current,
    }),
    [
      copyType,
      pasteNewLayer,
      selectedLayerId,
      selectedLayerType,
      setShowStocksModal,
      stocksNotFound,
      subStock,
      layerOperationType?.current,
    ],
  );

  const copyParams: useCopyParams = useMemo(
    () => ({
      setCopyType,
      setPasteDisable,
      setLayerOperationType,
    }),
    [setCopyType, setPasteDisable, setLayerOperationType],
  );

  const exp = useRMSelector((state) => state.experiment.expData);
  const Copy = useCopy(copyParams);
  const [Paste, pasteLayer] = usePaste(pasteParams);
  const [undoDisable, setUndoDisable] = useState<boolean>(false);
  const [redoDisable, setRedoDisable] = useState<boolean>(false);
  const pastData = useRMSelector((state) => state.experiment.past);
  const futureData = useRMSelector((state) => state.experiment.future);
  const [documentType, setDocumentType] = useState('');
  const isScreenRoute = window.location.pathname.includes('/screen');
  const Undo = useUndoExperiment();
  const Redo = useRedoExperiment();
  const _selectedLayerId = useRMSelector((state) => state.experiment.selectedLayerId);
  const _licenseEdition = useRMSelector((state) => state.plate.product.edition);
  const expDashboardContext = useContext(ExpDashboardContext);
  const isLayerSelected = useRMSelector(selectIsLayerSelected);
  const { isCanvasInFocus } = expDashboardContext;

  const styles: IStyle = {
    mainDivStyle: {
      marginLeft: 20,
      marginRight: 20,
    },
    mainModalBody: {
      display: 'flex',
      flexDirection: 'column',
    },
    targetTypeDiv: {
      marginBottom: '1rem',
      color: '#F89723',
      fontWeight: 700,
      fontSize: '1.5rem',
      fontFamily: 'Arial',
    },
    labelRadioBoxStyle: {
      justifyContent: 'flex-start',
    },
    selectLayerStyle: {
      fontWeight: 400,
      fontSize: '0.8125rem',
      fontFamily: 'Arial',
      padding: 5,
      flex: '1',
    },
    substituteTextStyle: {
      fontWeight: 400,
      fontSize: '0.8125rem',
      fontFamily: 'Arial',
      padding: 5,
      flex: '11',
    },
    buttonDivs: {
      display: 'flex',
      alignSelf: 'center',
      flexWrap: 'wrap',
      gap: '.5rem',
    },
  };

  const isCopyButtonEnabledLicenseEdition = useCallback(() => {
    let isCopyDisabled = false;
    if (_licenseEdition == EProductEditions.PlateViewer) {
      const selectedLayer = exp?.layers?.find((lyr) => lyr.id == _selectedLayerId);

      if (isScreenRoute) {
        if (selectedLayer && selectedLayer.type != LayerType.ConditionList) {
          isCopyDisabled = true;
        }
      } else {
        if (
          selectedLayer &&
          (selectedLayer.type == LayerType.Grid ||
            selectedLayer.type == LayerType.Random ||
            selectedLayer.type == LayerType.ConditionList)
        ) {
          isCopyDisabled = true;
        }
      }
    }
    return isCopyDisabled;
  }, [_selectedLayerId]);

  const selectedLayerChangeHandler = useCallback(
    (e) => {
      const lyr = exp.layers.find((lyr) => lyr.id == e.target.value);
      if (lyr) {
        setSelectedLayerId(lyr.id);
      }
    },
    [exp],
  );

  const newLayerTypeChangeHandler = useCallback(
    (e) => {
      const lyr = LAYER_TYPES.find((lt) => lt.value == e.target.value);
      if (lyr) {
        setSelectedLayerType(lyr.value);
      }
    },
    [LAYER_TYPES],
  );

  const checkPasteEnableState = async () => {
    let count = 0;
    const bufferCopyType = await BUFFER.ReadJSONCopyType();
    if (!bufferCopyType) {
      return;
    }
    let isPasteDisabled = true;
    if (bufferCopyType.length > 0) {
      if (bufferCopyType[0].value === 'Well') {
        await BUFFER.ReadJSON();
        count = await BUFFER.getCount();
        isPasteDisabled = count > 0 ? false : true;
      } else if (bufferCopyType[0].value === 'Layer') {
        isPasteDisabled = await isLayerPasteEnable();
      }
    }
    setPasteDisable(isPasteDisabled);
  };

  const isLayerPasteEnable = async () => {
    const count = await BUFFER.getLayerStoreCount();
    let isPasteDisabled = true;
    if (count == 0) {
      isPasteDisabled = true;
    }
    const copiedLayer = await BUFFER.ReadJSONLayers();

    if (documentType == 'Screen') {
      if (
        copiedLayer[0].type == LayerType.Grid ||
        copiedLayer[0].type == LayerType.Random ||
        copiedLayer[0].type == LayerType.ConditionList
      ) {
        isPasteDisabled = false;
      }
    } else if (documentType == 'Experiment') {
      isPasteDisabled = false;
    }

    return isPasteDisabled;
  };

  const onClickPaste = useCallback(async () => {
    const bufferCopyType = await BUFFER.ReadJSONCopyType();
    if (bufferCopyType.length > 0) {
      if (bufferCopyType[0].value == 'Layer') {
        const isLayerPasteDisabled = await isLayerPasteEnable();
        if (!isLayerPasteDisabled) {
          Paste();
        }
      } else {
        Paste();
      }
    }
  }, [Paste]);

  const experimentClipboardStates = useMemo(
    () => ({
      experimentCopy: () => Copy(CutCopyOperationType.Copy),
      experimentPaste: onClickPaste,
      isCopyDisabled:
        exp?.accessRightName !== ETreeNodeAccessRightNames.Modify ||
        isDisabled ||
        isCopyButtonEnabledLicenseEdition(),
      isPasteDisabled: pasteDisable || isPasteNotAllowed || isDisabled,
    }),
    [
      Copy,
      onClickPaste,
      pasteDisable,
      isPasteNotAllowed,
      isDisabled,
      exp?.accessRightName,
      isCopyButtonEnabledLicenseEdition,
    ],
  );

  setExperimentClipboardState(experimentClipboardStates);

  const isCopyPasteDisabled =
    exp?.accessRightName !== ETreeNodeAccessRightNames.Modify &&
    isDisabled &&
    isCopyButtonEnabledLicenseEdition();

  const isPasteDisabled = pasteDisable || isPasteNotAllowed || isDisabled;
  const isCutDisabled = isPasteNotAllowed || isDisabled;

  const handleUserKeyPress = (e) => {
    if (!isCanvasInFocus || !isCtrlKeyPressed(e) || isCopyPasteDisabled) {
      return;
    }

    const key = getNormalizedKeyboardKey(e);

    const tryAction = (action, isDisabled, errorMessage) => {
      if (isDisabled) {
        showAlertSnackBar(EAlertType.WARNING, ESnackBarPosition.TOP, errorMessage);
      } else {
        action();
      }
    };

    const actions = {
      [KeyboardKeys.X]: () =>
        isLayerSelected &&
        tryAction(
          () => Copy(CutCopyOperationType.Cut),
          isCutDisabled,
          'Cut is not allowed.',
        ),

      [KeyboardKeys.c]: () => Copy(CutCopyOperationType.Copy),

      [KeyboardKeys.v]: () =>
        tryAction(() => Paste(), isPasteDisabled, 'Paste is not allowed.'),
    };

    if (actions[key]) {
      actions[key]();
    }
  };

  useEffect(() => {
    checkPasteEnableState();
  }, [documentType]);

  useEffect(() => {
    if (pastData.length > 0) {
      setUndoDisable(false);
    } else {
      setUndoDisable(true);
    }
  }, [pastData]);

  useEffect(() => {
    if (futureData.length > 0) {
      setRedoDisable(false);
    } else {
      setRedoDisable(true);
    }
  }, [futureData]);

  useEffect(() => {
    if (isScreenRoute) {
      setDocumentType('Screen');
    } else {
      setDocumentType('Experiment');
    }

    const selectedLayer = exp?.layers
      ? exp?.layers.find(
          (lyr) =>
            lyr.type == LayerTypeDTO.Random || lyr.type == LayerTypeDTO.ConditionList,
        )
      : null;
    setSelectedLayerId(selectedLayer ? selectedLayer.id : 0);
  }, [exp]);

  useEventListener('keyup', handleUserKeyPress);

  const commandButtonsList: RMCustomButtonProps[] = [
    {
      iconProps: { id:'expcommandbuttonslist-cut',  name: 'Cut', fill: headerButtonsColor, ...headerIconSize },
      onClick: () => {
        Copy(CutCopyOperationType.Cut);
      },
      titleTip: 'Cut',
      isDisabled:
        exp?.accessRightName !== ETreeNodeAccessRightNames.Modify ||
        isDisabled ||
        isCopyButtonEnabledLicenseEdition() ||
        !isLayerSelected ||
        isCutDisabled,
    },
    {
      iconProps: { id:'expcommandbuttonslist-copy', name: 'Copy', fill: headerButtonsColor, ...headerIconSize },
      onClick: () => {
        Copy(CutCopyOperationType.Copy);
      },
      titleTip: 'Copy',
      isDisabled:
        exp?.accessRightName !== ETreeNodeAccessRightNames.Modify ||
        isDisabled ||
        isCopyButtonEnabledLicenseEdition(),
    },
    {
      iconProps: { id:'expcommandbuttonslist-paste', name: 'Paste', fill: headerButtonsColor, ...headerIconSize },
      onClick: onClickPaste,
      titleTip: 'Paste',
      isDisabled: pasteDisable || isPasteNotAllowed || isDisabled,
    },
    {
      iconProps: { id:'expcommandbuttonslist-reload', name: 'Reload', fill: headerButtonsColor, ...headerIconSize },
      isDisabled: true,
      titleTip: 'Reload',
      onClick: () => {},
    },
    {
      iconProps: { id:'expcommandbuttonslist-undo', name: 'Undo', fill: headerButtonsColor, ...headerIconSize },
      onClick: Undo,
      titleTip: 'Undo',
      isDisabled: undoDisable,
    },
    {
      iconProps: { id:'expcommandbuttonslist-redo', name: 'Redo', fill: headerButtonsColor, ...headerIconSize },
      onClick: Redo,
      titleTip: 'Redo',
      isDisabled: redoDisable,
    },
  ];
  return (
    <div id='experimentcommandbutton-main-container' style={styles.mainDivStyle}>
      <ButtonsListContainer
        containerStyleClass="commands-container"
        buttonsList={commandButtonsList}
      />
      <RMModal
        id="experimentcommandbutton-pastemodal"
        showModal={showPasteModal}
        setShowModal={setShowPasteModal}
        showButtons={true}
        heading="Target Type"
        onCancel={() => {
          setShowPasteModal(false);
          setStocksNotFound([]);
        }}
        okButtonText="APPLY"
        onOk={pasteLayer}
        body={
          <>
            <LabelRadioBox
              labelText={'Create New Layer'}
              radioValue={pasteNewLayer}
              onChange={() => setPasteNewLayer(true)}
              name="pasteAs"
              disabled={false}
              style={styles.labelRadioBoxStyle}
            />
            <Row id="experimentcommandbutton-row-createnewlayer" style={{ marginLeft: '1rem' }}>
              <Col id="experimentcommandbutton-col-createnewlayer">
                <div id="experimentcommandbutton-col-selectexistinglayer" className="row" style={{ margin: '10px 10px' }}>
                  <div id="experimentcommandbutton-selectexistinglayer-label" className="col-md-6" style={styles.selectLayerStyle}>
                    {'Select Layer'}
                  </div>

                  <div id="experimentcommandbutton-selectexistinglayer-dropdown-container" style={{ flex: '3', padding: '2px' }}>
                    <select
                      id="experimentcommandbutton-selectexistinglayer-dropdown"
                      disabled={!pasteNewLayer}
                      style={{ width: '9rem' }}
                      value={selectedLayerType}
                      onChange={newLayerTypeChangeHandler}
                    >
                      {LAYER_TYPES.map((lyr) => (
                        <option id={`experimentcommandbutton-selectexistinglayer-option-${lyr.name}`} key={lyr.value} value={lyr.value}>
                          {lyr.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </Col>
            </Row>
            {selectedLayerId != 0 && (
              <>
                <LabelRadioBox
                  labelText={'Add to Exsiting Layer'}
                  radioValue={!pasteNewLayer}
                  onChange={() => setPasteNewLayer(false)}
                  name="pasteAs"
                  disabled={false}
                  style={styles.labelRadioBoxStyle}
                />
                <Row id="experimentcommandbutton-row-addtoexistinglayer" style={{ marginLeft: '1rem' }}>
                  <Col id="experimentcommandbutton-col-addtoexistinglayer">
                    <div id="experimentcommandbutton-row-selectexistinglayer" className="row" style={{ margin: '10px 10px' }}>
                      <div id="experimentcommandbutton-selectexistinglayer-label" className="col-md-6" style={styles.selectLayerStyle}>
                        {'Select Layer'}
                      </div>
                      <div id="experimentcommandbutton-selectexistinglayer-dropdown-container" style={{ flex: '3', padding: '2px' }}>
                        <select
                          id="experimentcommandbutton-selectexistinglayer-dropdown"
                          disabled={pasteNewLayer}
                          style={{ width: '9rem' }}
                          value={selectedLayerId}
                          onChange={selectedLayerChangeHandler}
                          className="m-auto"
                        >
                          {exp.layers &&
                            exp.layers.map((lyr) => {
                              if (
                                lyr.type == LayerTypeDTO.Random ||
                                lyr.type == LayerTypeDTO.ConditionList
                              ) {
                                return (
                                  <option id={`experimentcommandbutton-selectexistinglayer-option-${lyr.name}`} key={lyr.id} value={lyr.id}>
                                    {lyr.name}
                                  </option>
                                );
                              }
                            })}
                        </select>
                      </div>
                    </div>
                  </Col>
                </Row>
              </>
            )}
            <Row id="experimentcommandbutton-row-substitutestock">
              <Col id="experimentcommandbutton-col-checkbox" style={{ marginLeft: '5px', flex: '1' }}>
                <CheckBox
                  id="experimentcommandbutton-checkbox-substitutestock"
                  style={{ backgroundColor: 'yellow' }}
                  isChecked={subStock}
                  onChange={() => setSubStock(!subStock)}
                />
              </Col>
              <Col id="experimentcommandbutton-col-substitutestock-text" style={styles.substituteTextStyle}>
                Substitute Source Stocks with Prepared Ones
              </Col>
            </Row>
            <br id="experimentcommandbutton-br-spacing" />
          </>
        }
      />
      <PreparedStocksWarningModal
        show={showStocksModal}
        setShow={setShowStocksModal}
        stocksList={stocksNotFound}
      />
    </div>
  );
};

export default ExperimentCommandButtons;
